/**
 * PlayerRig: PlayerRoot (position) + Camera (rotation) entity structure.
 * Applies a CameraState to the rig each frame.
 *
 * Hierarchy: root (position) → { avatarRoot, camera (local offset + rotation) }.
 * `avatarRoot` carries the visible character; the AvatarController attaches the
 * loaded GLB under it. The camera's local position is `state.cameraOffset`
 * (zero in first person, the orbit offset in third person) and its local
 * rotation is `state.angles`.
 */

import { Entity } from 'playcanvas';
import type { CameraState } from './types';

class PlayerRig {
    readonly root: Entity;
    readonly camera: Entity;
    readonly avatarRoot: Entity;

    constructor(root: Entity, camera: Entity) {
        this.root = root;
        this.camera = camera;

        // AvatarRoot sits under PlayerRoot so the character follows the player
        // position; its local Y offsets the model to feet-on-ground and its
        // local yaw is driven by the third-person controller (movement facing).
        this.avatarRoot = new Entity('AvatarRoot');
        this.root.addChild(this.avatarRoot);
    }

    apply(state: CameraState) {
        this.root.setPosition(state.position);
        // Local position (offset) before rotation — independent of camera yaw.
        this.camera.setLocalPosition(state.cameraOffset.x, state.cameraOffset.y, state.cameraOffset.z);
        this.camera.setLocalEulerAngles(state.angles.x, state.angles.y, state.angles.z);
        const cam = this.camera.camera;
        if (cam && cam.fov !== state.fov) {
            cam.fov = state.fov;
        }
    }
}

export { PlayerRig };
